import React, { useEffect } from "react";
import { FilterDialog } from "../../../feature-components/FilterDialog/FilterDialog.tsx";
import { FilterButton } from "../../../feature-components/FilterButton/FilterButton.tsx";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks.ts";
import { useDeepResearchService } from "../../../adapters/deep-research/state/hooks/useDeepResearchService.tsx";
import {
  useFullFilterOptionsState,
  useFiltersLoadingState,
  useFiltersState,
} from "../../../adapters/deep-research/state/hooks/stateHooks.ts";
import {
  FilterState,
  ResearchId,
} from "../../../adapters/deep-research/state/AppState.ts";

export const ConnectedFilterDialog: React.FC<{
  researchId: ResearchId;
  knowledgeSourceName?: string;
  disabled?: boolean;
  onFiltersChange?: (filters: FilterState) => void;
  initialFilters?: FilterState;
}> = (props) => {
  const [open, setOpen] = React.useState(false);
  const appService = useDeepResearchService();
  const filterState = useFiltersState(props.researchId);
  const filtersLoading = useFiltersLoadingState();
  const filterOptionsState = useFullFilterOptionsState();

  const isNewResearch = props.researchId === "new";

  // For new research, use filterOptions.loading; for existing research, use filters.loading
  const loading = isNewResearch
    ? filterOptionsState.loading
    : filtersLoading || filterOptionsState.loading;

  useEffect(() => {
    if (isNewResearch && props.knowledgeSourceName) {
      // For new research, fetch filters by knowledge source name
      appService.getFiltersByKnowledgeSource({
        knowledgeSourceName: props.knowledgeSourceName,
      });
    } else if (!isNewResearch) {
      // For existing research, fetch filters by research ID
      appService.refreshFilters({
        researchId: props.researchId,
      });
      appService.refreshFilterOptions({
        researchId: props.researchId,
      });
    }
  }, [appService, props.researchId, props.knowledgeSourceName, isNewResearch]);

  const enableDynamicFilters = useFeatureFlagValue("enableDynamicFilters");

  const isDisabled =
    props.disabled || (isNewResearch && !props.knowledgeSourceName);

  return (
    <>
      <FilterButton onClick={() => setOpen(true)} disabled={isDisabled} />
      {open && (
        <FilterDialog
          onClose={() => setOpen(false)}
          legacyFilterOptions={filterOptionsState.items}
          newFilterOptions={
            enableDynamicFilters ? filterOptionsState.list : undefined
          }
          filterState={isNewResearch ? props.initialFilters || {} : filterState}
          loading={loading}
          onApply={(filters) => {
            if (!isNewResearch) {
              appService
                .setFilters({
                  researchId: props.researchId,
                  filters,
                })
                .then(() => {
                  setOpen(false);
                });
            } else {
              props.onFiltersChange?.(filters);
              setOpen(false);
            }
          }}
          onClear={() => {
            if (!isNewResearch) {
              appService
                .setFilters({
                  researchId: props.researchId,
                  filters: {},
                })
                .then(() => {
                  setOpen(false);
                });
            } else {
              props.onFiltersChange?.({});
              setOpen(false);
            }
          }}
        />
      )}
    </>
  );
};
